/**
 * Local persistence for the player identity + the offline-submit queue.
 *
 * Web uses `localStorage`. Capacitor native shells will swap this for
 * `@capacitor/preferences` later (Phase 4) — same async signature so callers
 * don't change.
 */
import type { StoredPlayer, RunResult } from './types.js';

const KEYS = {
  player: 'neonix.lb.player',
  pendingRuns: 'neonix.lb.pendingRuns',
  archive: 'neonix.lb.playerArchive',
} as const;

/** How many past players to retain in the archive. Older entries roll off. */
const ARCHIVE_MAX = 5;

/** A previously-active player record, preserved for one-tap recovery. */
export interface ArchivedPlayer extends StoredPlayer {
  /** Wall-clock ms when this record was archived. */
  archivedAt: number;
}

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function loadPlayer(): StoredPlayer | null {
  return safeParse<StoredPlayer>(localStorage.getItem(KEYS.player));
}

export function savePlayer(player: StoredPlayer): void {
  localStorage.setItem(KEYS.player, JSON.stringify(player));
}

export function clearPlayer(): void {
  localStorage.removeItem(KEYS.player);
}

/**
 * Move the current active player into the archive instead of destroying
 * it. This is the durable replacement for `clearPlayer` on the self-heal
 * path: the active slot is freed (so a fresh signup can occupy it) but
 * the previous identity is preserved alongside its bearer + recovery
 * phrase, so the user can one-tap restore it from Settings if the
 * self-heal misfired.
 *
 * No-op when there's no active player. Archive is capped at ARCHIVE_MAX
 * — oldest entries roll off so localStorage doesn't grow unboundedly.
 * De-dupes on `id` so a flapping bearer doesn't fill the archive with
 * the same player.
 */
export function archivePlayer(): void {
  const current = loadPlayer();
  if (!current) return;
  const archive = loadArchive().filter((p) => p.id !== current.id);
  archive.unshift({ ...current, archivedAt: Date.now() });
  setArchive(archive.slice(0, ARCHIVE_MAX));
  clearPlayer();
}

export function loadArchive(): ArchivedPlayer[] {
  return safeParse<ArchivedPlayer[]>(localStorage.getItem(KEYS.archive)) ?? [];
}

function setArchive(entries: ArchivedPlayer[]): void {
  if (entries.length === 0) {
    localStorage.removeItem(KEYS.archive);
  } else {
    localStorage.setItem(KEYS.archive, JSON.stringify(entries));
  }
}

/**
 * Restore a previously-archived player to the active slot. Used by the
 * "Previous accounts on this device" UI in Settings. The current active
 * player (if any) is itself archived first, so a misclick doesn't
 * destroy whatever's currently active.
 */
export function restoreArchivedPlayer(id: string): ArchivedPlayer | null {
  const archive = loadArchive();
  const target = archive.find((p) => p.id === id);
  if (!target) return null;
  archivePlayer();
  // Strip the archive metadata before saving back as the active player.
  const { archivedAt: _archivedAt, ...active } = target;
  savePlayer(active);
  setArchive(loadArchive().filter((p) => p.id !== id));
  return target;
}

/**
 * Append a run to the offline queue when the network is unavailable.
 * The next successful `submitRun` flushes the queue first.
 */
export function queueRun(run: RunResult): void {
  const existing = safeParse<RunResult[]>(localStorage.getItem(KEYS.pendingRuns)) ?? [];
  // Cap at 20 — older queued runs are dropped silently. We don't want a
  // permanent offline player to grow this unboundedly.
  const next = [...existing, run].slice(-20);
  localStorage.setItem(KEYS.pendingRuns, JSON.stringify(next));
}

export function loadQueuedRuns(): RunResult[] {
  return safeParse<RunResult[]>(localStorage.getItem(KEYS.pendingRuns)) ?? [];
}

export function setQueuedRuns(runs: RunResult[]): void {
  if (runs.length === 0) {
    localStorage.removeItem(KEYS.pendingRuns);
  } else {
    localStorage.setItem(KEYS.pendingRuns, JSON.stringify(runs));
  }
}
